import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, MessageCircle, Plus, TrendingUp, User, LogOut, Menu, X, FileText } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/home", label: "Today", icon: Home, testid: "nav-home" },
  { to: "/memora", label: "Memora", icon: MessageCircle, testid: "nav-memora" },
  { to: "/add", label: "Add", icon: Plus, testid: "nav-add" },
  { to: "/trends", label: "Trends", icon: TrendingUp, testid: "nav-trends" },
  { to: "/records", label: "Records", icon: FileText, testid: "nav-records" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 flex">
      {/* Sidebar (md+) */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:fixed md:inset-y-0 md:border-r md:border-neutral-200 md:bg-white">
        <div className="px-6 py-6 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F6CFBE] to-[#B9DCF2]" />
            <div className="font-semibold text-lg tracking-tight">Memora</div>
          </div>
          <p className="text-xs text-neutral-500 mt-1">Your health, remembered.</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-700 hover:bg-neutral-100"
                )
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-neutral-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-semibold">
              {(user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || "You"}</div>
              <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
            </div>
            <button data-testid="btn-logout" onClick={doLogout} className="p-2 rounded hover:bg-neutral-100" title="Log out">
              <LogOut className="w-4 h-4 text-neutral-500" />
            </button>
          </div>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-neutral-200 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F6CFBE] to-[#B9DCF2]" />
          <div className="font-semibold">Memora</div>
        </div>
        <button data-testid="btn-menu" onClick={() => setOpen(!open)} className="p-2 rounded hover:bg-neutral-100">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>
      {open && (
        <div className="md:hidden fixed inset-0 top-14 z-20 bg-white">
          <nav className="p-3 space-y-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`m-${n.testid}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-3 rounded-lg text-base",
                    isActive ? "bg-neutral-900 text-white" : "text-neutral-800 hover:bg-neutral-100"
                  )
                }
              >
                <n.icon className="w-5 h-5" />
                {n.label}
              </NavLink>
            ))}
            <button data-testid="m-btn-logout" onClick={doLogout} className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-base text-neutral-800 hover:bg-neutral-100">
              <LogOut className="w-5 h-5" />
              Log out
            </button>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 md:pl-64 pt-14 md:pt-0">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-6 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
